/**
 * sl-xk37.8: MCP Access Management Panel
 *
 * Workspace-scoped MCP token management UI. Admins/Owners can:
 * - View all active MCP tokens (user, role, masked token, creator, dates)
 * - Grant MCP access to workspace members with configurable expiration
 * - Revoke tokens with confirmation
 * - Copy ready-to-use MCP config JSON after generation
 *
 * SOLID:
 * - S: Only MCP token CRUD display and actions
 * - D: Depends on GraphQLService for all data access
 */

import {
  Button,
  ConfirmModal,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  Skeleton,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { getOfflineStatusCopy } from '@affine/core/components/offline-status';
import { GraphQLService, ServerService } from '@affine/core/modules/cloud';
import { useObjectPermissionOnlineOnlyState } from '@affine/core/modules/permissions/utils/object-permission-ui';
import {
  PermissionMemberInviteSearch,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { UserFriendlyError } from '@affine/error';
import {
  generateWorkspaceMcpTokenMutation,
  listWorkspaceMcpTokensQuery,
  revokeWorkspaceMcpTokenMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsCodeBlock,
  SettingsContentSection,
  SettingsContentText,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import { SettingsEntityPage } from '../shared/settings-entity-page';
import { useSettingsEntityList } from '../shared/use-settings-entity-list';
import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import {
  formatWorkspaceRole,
  type McpAvailableToolsResponse,
  type McpToken,
  McpTokenRow,
  McpToolsPreview,
  translateOptional,
  type WorkspaceMcpToolPolicy,
} from './mcp-review';
import * as styles from './styles.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkspaceMember {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  permission: string;
}

type ExpirationOption = {
  labelKey: string;
  fallback: string;
  value: number | null;
};

const EXPIRATION_OPTIONS: ExpirationOption[] = [
  {
    labelKey: 'com.streamlain.settings.workspace.mcp-access.expiration.7-days',
    fallback: '7 days',
    value: 7,
  },
  {
    labelKey: 'com.streamlain.settings.workspace.mcp-access.expiration.30-days',
    fallback: '30 days',
    value: 30,
  },
  {
    labelKey: 'com.streamlain.settings.workspace.mcp-access.expiration.90-days',
    fallback: '90 days',
    value: 90,
  },
  {
    labelKey:
      'com.streamlain.settings.workspace.mcp-access.expiration.unlimited',
    fallback: 'Unlimited',
    value: null,
  },
];

type PendingTokenReplacement = {
  userId: string;
  expirationDays: number | null;
  userLabel: string;
  toolPolicy: WorkspaceMcpToolPolicy;
};

type SettingsSelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

const EMPTY_EXISTING_USER_IDS: readonly string[] = [];

function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  testId,
}: {
  value: T;
  options: ReadonlyArray<SettingsSelectOption<T>>;
  onChange: (value: T) => void;
  testId: string;
}) {
  const current = options.find(option => option.value === value) ?? options[0];
  return (
    <Menu
      contentOptions={{ align: 'start' }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          selected={option.value === value}
          disabled={option.disabled}
          onSelect={() => {
            if (!option.disabled) {
              onChange(option.value);
            }
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    >
      <MenuTrigger
        block
        className={styles.formSelect}
        data-testid={testId}
        disabled={options.every(option => option.disabled)}
      >
        {current?.label ?? ''}
      </MenuTrigger>
    </Menu>
  );
}

function mergeMembers(
  existing: WorkspaceMember[],
  incoming: WorkspaceMember[]
): WorkspaceMember[] {
  const byId = new Map(existing.map(member => [member.id, member]));
  let changed = false;
  for (const member of incoming) {
    const current = byId.get(member.id);
    if (
      !current ||
      current.name !== member.name ||
      current.email !== member.email ||
      current.avatarUrl !== member.avatarUrl ||
      current.permission !== member.permission
    ) {
      changed = true;
    }
    byId.set(member.id, member);
  }
  return changed ? Array.from(byId.values()) : existing;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const McpAccessPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const permissionOnlineOnly = useObjectPermissionOnlineOnlyState({
    backendUnavailable: !backendAvailable,
  });
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const gqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(ServerService);
  const workspaceId = workspaceService.workspace.id;
  const workspaceName = useLiveData(workspaceService.workspace.name$);

  // -------------------------------------------------------------------------
  // Token list via shared hook
  // -------------------------------------------------------------------------

  const {
    data: tokens,
    isLoading,
    error,
    refresh: refreshTokens,
  } = useSettingsEntityList<McpToken>(async () => {
    if (!backendAvailable) {
      return [];
    }
    try {
      const result = await gqlService.gql({
        query: listWorkspaceMcpTokensQuery,
        variables: { workspaceId },
      });
      return (result as any).workspaceMcpTokens ?? [];
    } catch (err) {
      if (isBackendUnavailableError(err)) {
        markBackendUnavailable(err);
      }
      throw err;
    }
  }, [backendAvailable, gqlService, workspaceId]);

  // -------------------------------------------------------------------------
  // Grant form state
  // -------------------------------------------------------------------------

  const [memberCache, setMemberCache] = useState<WorkspaceMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [memberSearchValue, setMemberSearchValue] = useState('');
  const [expirationDays, setExpirationDays] = useState<number | null>(30);
  const [generating, setGenerating] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState<string | null>(null);
  const [availableTools, setAvailableTools] =
    useState<McpAvailableToolsResponse | null>(null);
  const [availableToolsLoading, setAvailableToolsLoading] = useState(false);
  const [availableToolsError, setAvailableToolsError] = useState<string | null>(
    null
  );
  const [disabledToolCategories, setDisabledToolCategories] = useState<
    Set<string>
  >(() => new Set());
  const [disabledTools, setDisabledTools] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingTokenReplacement, setPendingTokenReplacement] =
    useState<PendingTokenReplacement | null>(null);
  const memberPickerUnavailableMessage = getOfflineStatusCopy(
    t,
    'people-online-only'
  );

  const selectedExistingToken = useMemo(
    () => (tokens ?? []).find(token => token.userId === selectedUserId) ?? null,
    [selectedUserId, tokens]
  );

  const memberById = useMemo(() => {
    const map = new Map<string, WorkspaceMember>();
    for (const m of memberCache) map.set(m.id, m);
    return map;
  }, [memberCache]);

  // MCP endpoint — backend runs on port 3010 in dev, same host in prod
  const mcpBaseUrl = useMemo(() => {
    const baseUrl = serverService.server.baseUrl;
    try {
      const url = new URL(baseUrl);
      if (url.port === '8080') {
        url.port = '3010';
      }
      return url.toString().replace(/\/$/, '');
    } catch {
      return baseUrl;
    }
  }, [serverService.server.baseUrl]);

  const translate = useCallback(
    (key: string, fallback: string, options?: Record<string, string>) =>
      translateOptional(t, key, fallback, options),
    [t]
  );
  const memberFallback = translate(
    'com.streamlain.settings.workspace.mcp-access.member-fallback',
    'this member'
  );

  const formatRole = useCallback(
    (role: string) => formatWorkspaceRole(t, role),
    [t]
  );

  useEffect(() => {
    const toolPolicy = selectedExistingToken?.toolPolicy;
    setDisabledToolCategories(new Set(toolPolicy?.disabledCategories ?? []));
    setDisabledTools(new Set(toolPolicy?.disabledTools ?? []));
  }, [
    selectedExistingToken?.id,
    selectedExistingToken?.toolPolicy,
    selectedUserId,
  ]);

  const selectedToolPolicy = useMemo<WorkspaceMcpToolPolicy>(
    () => ({
      disabledCategories: Array.from(disabledToolCategories).sort(),
      disabledTools: Array.from(disabledTools).sort(),
    }),
    [disabledToolCategories, disabledTools]
  );

  const toolNamesByCategory = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of availableTools?.groups ?? []) {
      map.set(
        group.category,
        group.tools.map(tool => tool.name)
      );
    }
    return map;
  }, [availableTools?.groups]);

  const handleToggleToolCategory = useCallback(
    (category: string) => {
      const categoryToolNames = toolNamesByCategory.get(category) ?? [];
      const categoryDisabled = disabledToolCategories.has(category);
      const categoryAllEnabled =
        !categoryDisabled &&
        categoryToolNames.length > 0 &&
        categoryToolNames.every(toolName => !disabledTools.has(toolName));
      const shouldDisableCategory =
        categoryToolNames.length === 0 ? !categoryDisabled : categoryAllEnabled;

      setDisabledToolCategories(prev => {
        const next = new Set(prev);
        if (shouldDisableCategory) {
          next.add(category);
        } else {
          next.delete(category);
        }
        return next;
      });
      setDisabledTools(prev => {
        const next = new Set(prev);
        for (const toolName of categoryToolNames) {
          next.delete(toolName);
        }
        return next;
      });
    },
    [disabledToolCategories, disabledTools, toolNamesByCategory]
  );

  const handleToggleTool = useCallback(
    (toolName: string, category: string) => {
      const knownToolNames = toolNamesByCategory.get(category) ?? [];
      const categoryToolNames = knownToolNames.includes(toolName)
        ? knownToolNames
        : [...knownToolNames, toolName];
      const categoryDisabled = disabledToolCategories.has(category);

      if (categoryDisabled) {
        setDisabledToolCategories(prev => {
          const next = new Set(prev);
          next.delete(category);
          return next;
        });
      }

      setDisabledTools(prev => {
        const next = new Set(prev);
        if (categoryDisabled) {
          for (const candidate of categoryToolNames) {
            if (candidate === toolName) {
              next.delete(candidate);
            } else {
              next.add(candidate);
            }
          }
          return next;
        }
        if (next.has(toolName)) {
          next.delete(toolName);
        } else {
          next.add(toolName);
        }
        return next;
      });
    },
    [disabledToolCategories, toolNamesByCategory]
  );

  useEffect(() => {
    if (!selectedUserId) {
      setAvailableTools(null);
      setAvailableToolsError(null);
      setAvailableToolsLoading(false);
      return;
    }
    if (!backendAvailable) {
      setAvailableTools(null);
      setAvailableToolsLoading(false);
      setAvailableToolsError(null);
      return;
    }

    const controller = new AbortController();
    setAvailableToolsLoading(true);
    setAvailableToolsError(null);

    const url = `/api/workspaces/${workspaceId}/mcp-tools/available?targetUserId=${encodeURIComponent(
      selectedUserId
    )}`;
    void requestWorkspaceSetting(workspaceId, url, {
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error(await response.text());
        }
        return response.json() as Promise<McpAvailableToolsResponse>;
      })
      .then(data => {
        setAvailableTools(data);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        setAvailableTools(null);
        setAvailableToolsError(
          error instanceof Error ? error.message : String(error)
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAvailableToolsLoading(false);
        }
      });

    return () => controller.abort();
  }, [backendAvailable, selectedUserId, workspaceId]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleVisibleMembersChange = useCallback(
    (members: WorkspaceMember[]) => {
      setMemberCache(prev => mergeMembers(prev, members));
    },
    []
  );

  const handleSelectMember = useCallback((member: WorkspaceMember) => {
    setMemberCache(prev => mergeMembers(prev, [member]));
    setSelectedUserId(member.id);
    setMemberSearchValue('');
    setGeneratedConfig(null);
  }, []);

  const handleClearSelectedMember = useCallback(() => {
    setSelectedUserId('');
    setMemberSearchValue('');
    setGeneratedConfig(null);
  }, []);

  const generateToken = useCallback(
    async ({
      targetUserId,
      expirationDays,
      replaceExisting,
      toolPolicy,
    }: {
      targetUserId: string;
      expirationDays: number | null;
      replaceExisting: boolean;
      toolPolicy: WorkspaceMcpToolPolicy;
    }) => {
      setGenerating(true);
      try {
        const result = await gqlService.gql({
          query: generateWorkspaceMcpTokenMutation,
          variables: {
            input: {
              workspaceId,
              targetUserId,
              expirationDays,
              replaceExisting,
              toolPolicy,
            } as any,
          },
        });
        const data = (result as any).generateWorkspaceMcpToken;
        if (data?.config) {
          setGeneratedConfig(data.config);
        } else {
          const token = data?.token;
          if (token) {
            const config = JSON.stringify(
              {
                mcpServers: {
                  [`${workspaceName} - ${BRAND_DISPLAY_NAME}`]: {
                    type: 'http',
                    url: `${mcpBaseUrl}/api/workspaces/${workspaceId}/mcp`,
                    headers: {
                      Authorization: `Bearer <token shown only once>`,
                    },
                  },
                },
              },
              null,
              2
            );
            setGeneratedConfig(config);
          }
        }
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.mcp-access.granted-toast'
            ]?.() ?? 'MCP access granted successfully',
        });
        await refreshTokens();
      } catch (err) {
        if (isBackendUnavailableError(err)) {
          markBackendUnavailable(err);
        }
        notify.error({ error: UserFriendlyError.fromAny(err) });
      } finally {
        setGenerating(false);
      }
    },
    [gqlService, workspaceId, workspaceName, mcpBaseUrl, refreshTokens, t]
  );

  const handleGenerate = useAsyncCallback(async () => {
    if (!selectedUserId) return;
    const existingToken = selectedExistingToken;
    const selectedMember = memberById.get(selectedUserId);
    if (existingToken) {
      setPendingTokenReplacement({
        userId: selectedUserId,
        expirationDays,
        userLabel:
          selectedMember?.name ??
          selectedMember?.email ??
          existingToken.userEmail ??
          memberFallback,
        toolPolicy: selectedToolPolicy,
      });
      return;
    }
    await generateToken({
      targetUserId: selectedUserId,
      expirationDays,
      replaceExisting: false,
      toolPolicy: selectedToolPolicy,
    });
  }, [
    selectedUserId,
    expirationDays,
    selectedExistingToken,
    memberById,
    memberFallback,
    generateToken,
    selectedToolPolicy,
  ]);

  const handleConfirmReplacement = useCallback(async () => {
    const pending = pendingTokenReplacement;
    if (!pending) return;
    setPendingTokenReplacement(null);
    await generateToken({
      targetUserId: pending.userId,
      expirationDays: pending.expirationDays,
      replaceExisting: true,
      toolPolicy: pending.toolPolicy,
    });
  }, [generateToken, pendingTokenReplacement]);

  const handleCancelReplacement = useCallback(() => {
    setPendingTokenReplacement(null);
  }, []);

  const handleRevoke = useCallback(
    async (tokenId: string) => {
      try {
        await gqlService.gql({
          query: revokeWorkspaceMcpTokenMutation,
          variables: { workspaceId, tokenId },
        });
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.mcp-access.revoked-toast'
            ]?.() ?? 'MCP token revoked',
        });
        await refreshTokens();
      } catch (err) {
        if (isBackendUnavailableError(err)) {
          markBackendUnavailable(err);
        }
        notify.error({ error: UserFriendlyError.fromAny(err) });
      }
    },
    [gqlService, workspaceId, refreshTokens, t]
  );

  const handleCopyConfig = useCallback(() => {
    if (!generatedConfig) return;
    void navigator.clipboard.writeText(generatedConfig);
    notify.success({
      title:
        t['com.streamlain.settings.workspace.mcp-access.copied-toast']?.() ??
        'Copied to clipboard',
    });
  }, [generatedConfig, t]);

  const expirationSelectValue =
    expirationDays === null ? 'unlimited' : String(expirationDays);
  const expirationSelectOptions = useMemo<Array<SettingsSelectOption<string>>>(
    () =>
      EXPIRATION_OPTIONS.map(option => ({
        value: option.value === null ? 'unlimited' : String(option.value),
        label:
          t[
            option.labelKey as 'com.streamlain.settings.workspace.mcp-access.expiration.7-days'
          ]?.() ?? option.fallback,
      })),
    [t]
  );

  // -------------------------------------------------------------------------
  // Render: Loading
  // -------------------------------------------------------------------------

  const showOfflineNotice = permissionOnlineOnly.disabled;

  if (isLoading && tokens === undefined && !showOfflineNotice) {
    return (
      <div>
        <Skeleton />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Error
  // -------------------------------------------------------------------------

  if (error && tokens === undefined) {
    return (
      <SettingsEntityPage
        title={
          t['com.streamlain.settings.workspace.mcp-access.title']?.() ??
          'MCP Access'
        }
        headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
      >
        <SettingsEntityMessage>{error}</SettingsEntityMessage>
      </SettingsEntityPage>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Main
  // -------------------------------------------------------------------------

  const tokenList = tokens ?? [];
  const title =
    t['com.streamlain.settings.workspace.mcp-access.title']?.() ?? 'MCP Access';
  const selectedMember = selectedUserId ? memberById.get(selectedUserId) : null;
  const selectedUserName =
    selectedMember?.name ??
    selectedMember?.email ??
    selectedExistingToken?.userName ??
    selectedExistingToken?.userEmail ??
    '';
  const selectedUserEmail =
    selectedMember?.email ?? selectedExistingToken?.userEmail ?? undefined;
  const selectedUserRole =
    selectedMember?.permission ?? selectedExistingToken?.userRole ?? 'Reader';
  const selectedAccessActive = Boolean(selectedExistingToken);

  return (
    <SettingsEntityPage
      title={title}
      headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
      subtitle={
        t['com.streamlain.settings.workspace.mcp-access.subtitle']?.() ??
        "Manage MCP access tokens for workspace members. Each token grants access to the MCP endpoint with the member's workspace role permissions."
      }
    >
      <SettingsContentSection
        title={translate(
          'com.streamlain.settings.workspace.mcp-access.member-search-title',
          'Select member'
        )}
      >
        <PermissionMemberInviteSearch
          title={translate(
            'com.streamlain.settings.workspace.mcp-access.member',
            'Member'
          )}
          placeholder={translate(
            'com.streamlain.settings.workspace.mcp-access.member-search-placeholder',
            'Search members...'
          )}
          noResultsText={translate(
            'com.streamlain.settings.workspace.mcp-access.no-members',
            'No members found'
          )}
          offlineMessage={memberPickerUnavailableMessage}
          roleSlot={null}
          existingUserIds={EMPTY_EXISTING_USER_IDS}
          disabled={permissionOnlineOnly.disabled}
          searchText={memberSearchValue}
          onSearchTextChange={setMemberSearchValue}
          onSelectMember={member => handleSelectMember(member)}
          onVisibleMembersChange={handleVisibleMembersChange}
          acceptedOnly={false}
          maxVisibleItems={8}
          showInitialResults={!selectedUserId}
          preserveResultsWhileSearching
        >
          {selectedUserId ? (
            <PermissionMemberRow
              user={{
                id: selectedUserId,
                name: selectedUserName,
                email: selectedUserEmail,
                avatarUrl: selectedMember?.avatarUrl ?? null,
              }}
              roleSelector={
                <span className={styles.memberRoleLabel}>
                  {formatRole(selectedUserRole)}
                </span>
              }
              onRemove={handleClearSelectedMember}
            />
          ) : null}
        </PermissionMemberInviteSearch>
      </SettingsContentSection>

      {selectedUserId ? (
        <SettingsContentSection
          title={translate(
            'com.streamlain.settings.workspace.mcp-access.selected-status-title',
            'Selected member MCP access'
          )}
        >
          <div
            className={styles.accessStatusCard}
            data-active={selectedAccessActive ? 'true' : 'false'}
          >
            <div className={styles.accessStatusHeader}>
              <div className={styles.accessStatusTitle}>
                {selectedAccessActive
                  ? translate(
                      'com.streamlain.settings.workspace.mcp-access.status-active-title',
                      'MCP access active'
                    )
                  : translate(
                      'com.streamlain.settings.workspace.mcp-access.status-inactive-title',
                      'No MCP access'
                    )}
              </div>
              <SettingsBadge variant={selectedAccessActive ? 'active' : 'soft'}>
                {selectedAccessActive
                  ? translate(
                      'com.streamlain.settings.workspace.mcp-access.status-active',
                      'Active'
                    )
                  : translate(
                      'com.streamlain.settings.workspace.mcp-access.status-inactive',
                      'Not granted'
                    )}
              </SettingsBadge>
            </div>
            <div className={styles.accessStatusText}>
              {selectedAccessActive
                ? translate(
                    'com.streamlain.settings.workspace.mcp-access.status-active-description',
                    'This member already has an MCP token. Creating a new token replaces the existing one.'
                  )
                : translate(
                    'com.streamlain.settings.workspace.mcp-access.status-inactive-description',
                    'Create an MCP token for this member if they should use workspace tools from an MCP client.'
                  )}
            </div>
          </div>

          {selectedExistingToken ? (
            <SettingsEntityList>
              <McpTokenRow
                token={selectedExistingToken}
                avatarUrl={selectedMember?.avatarUrl ?? null}
                onRevoke={handleRevoke}
              />
            </SettingsEntityList>
          ) : null}

          <div className={styles.formRow}>
            <SettingsSelect
              value={expirationSelectValue}
              options={expirationSelectOptions}
              onChange={value => {
                setExpirationDays(value === 'unlimited' ? null : Number(value));
              }}
              testId="mcp-access-expiration-select"
            />

            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={generating || showOfflineNotice}
              data-testid="mcp-access-generate-btn"
            >
              {generating
                ? translate(
                    'com.streamlain.settings.workspace.mcp-access.generating',
                    'Generating...'
                  )
                : selectedExistingToken
                  ? translate(
                      'com.streamlain.settings.workspace.mcp-access.replace-token',
                      'Replace token'
                    )
                  : translate(
                      'com.streamlain.settings.workspace.mcp-access.create-token',
                      'Create access'
                    )}
            </Button>

            <Button variant="secondary" onClick={handleClearSelectedMember}>
              {translate(
                'com.streamlain.settings.workspace.mcp-access.clear-selection',
                'Clear selection'
              )}
            </Button>
          </div>

          <McpToolsPreview
            key={selectedUserId}
            role={availableTools?.role ?? selectedUserRole}
            groups={availableTools?.groups}
            loading={availableToolsLoading}
            error={availableToolsError}
            disabledCategories={disabledToolCategories}
            disabledTools={disabledTools}
            onToggleCategory={handleToggleToolCategory}
            onToggleTool={handleToggleTool}
          />

          {generatedConfig && (
            <SettingsContentSection
              title={
                t[
                  'com.streamlain.settings.workspace.mcp-access.server-config'
                ]?.() ?? 'Server Config'
              }
            >
              <SettingsCodeBlock as="pre">{generatedConfig}</SettingsCodeBlock>
              <Button
                variant="primary"
                onClick={handleCopyConfig}
                data-testid="mcp-access-copy-json-btn"
              >
                {t[
                  'com.streamlain.settings.workspace.mcp-access.copy-json'
                ]?.() ?? 'Copy JSON'}
              </Button>
            </SettingsContentSection>
          )}
        </SettingsContentSection>
      ) : (
        <SettingsEntityMessage>
          <SettingsContentText>
            {translate(
              'com.streamlain.settings.workspace.mcp-access.select-member-empty',
              'Select a workspace member to see whether MCP access already exists and to manage their token.'
            )}
          </SettingsContentText>
        </SettingsEntityMessage>
      )}

      {tokenList.length > 0 ? (
        <SettingsContentSection
          title={translate(
            'com.streamlain.settings.workspace.mcp-access.active-list-title',
            'Active MCP access'
          )}
        >
          <SettingsEntityList>
            {tokenList.map(token => (
              <McpTokenRow
                key={token.id}
                token={token}
                avatarUrl={memberById.get(token.userId)?.avatarUrl ?? null}
                onRevoke={handleRevoke}
              />
            ))}
          </SettingsEntityList>
        </SettingsContentSection>
      ) : (
        <SettingsContentSection
          title={translate(
            'com.streamlain.settings.workspace.mcp-access.active-list-title',
            'Active MCP access'
          )}
        >
          <SettingsEntityMessage>
            <SettingsContentText>
              {showOfflineNotice
                ? offlineLabel
                : (t[
                    'com.streamlain.settings.workspace.mcp-access.empty'
                  ]?.() ?? 'No MCP access tokens have been granted yet.')}
            </SettingsContentText>
          </SettingsEntityMessage>
        </SettingsContentSection>
      )}

      <ConfirmModal
        open={pendingTokenReplacement !== null}
        onOpenChange={open => {
          if (!open) handleCancelReplacement();
        }}
        title={translate(
          'com.streamlain.settings.workspace.mcp-access.replace-confirm-title',
          'Replace MCP token'
        )}
        description={translate(
          'com.streamlain.settings.workspace.mcp-access.replace-confirm',
          `This member already has an MCP token. Generating a new token will revoke the existing token for ${
            pendingTokenReplacement?.userLabel ?? memberFallback
          }. Continue?`,
          { user: pendingTokenReplacement?.userLabel ?? memberFallback }
        )}
        confirmText={
          generating
            ? (t[
                'com.streamlain.settings.workspace.mcp-access.generating'
              ]?.() ?? 'Generating...')
            : translate(
                'com.streamlain.settings.workspace.mcp-access.replace-confirm-action',
                'Replace token'
              )
        }
        cancelText={t['Cancel']?.() ?? 'Cancel'}
        confirmButtonOptions={{
          variant: 'primary',
          disabled: generating,
        }}
        onConfirm={() => void handleConfirmReplacement()}
      />
    </SettingsEntityPage>
  );
};
